import React, { useEffect, useState } from "react";
import axios from "axios";
import { Table, Form, Container, Button, Badge, Alert, Spinner } from "react-bootstrap";
import { 
  FaSearch, 
  FaWifi, 
  FaPrint, 
  FaUndo,
  FaBan  // Using FaBan as an alternative for offline/wifi-slash
} from "react-icons/fa";
import { format } from "date-fns";
import TopNavbar from "../components/TopNavbar";
import RefundModal from "../components/RefundModal";

const SalesList = () => {
  const [allSales, setAllSales] = useState([]);
  const [offlineSales, setOfflineSales] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [user, setUser] = useState(null);
  const [theme, setTheme] = useState(localStorage.getItem("theme") || "light");
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [selectedSale, setSelectedSale] = useState(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [filteredSales, setFilteredSales] = useState([]);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  const baseURL = process.env.REACT_APP_BASE_URL;
  const token = localStorage.getItem("token");
  const OFFLINE_SALES_KEY = 'offline_sales';
  const SALES_CACHE_KEY = 'cached_sales';
  const PAGE_SIZE = 20;

  // Debounce search term
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  // Handle online/offline status
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      fetchSales(1, true);
      syncOfflineSales();
    };

    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Fetch sales data
  useEffect(() => {
    const storedUser = localStorage.getItem("login_user");
    if (storedUser) setUser(JSON.parse(storedUser));

    const loadCachedData = () => {
      const cachedSales = JSON.parse(localStorage.getItem(SALES_CACHE_KEY)) || [];
      const storedOfflineSales = JSON.parse(localStorage.getItem(OFFLINE_SALES_KEY)) || [];
      setAllSales(cachedSales);
      setOfflineSales(storedOfflineSales);
    };

    loadCachedData();
    fetchSales();
  }, []);

  const syncOfflineSales = async () => {
    if (!isOnline || offlineSales.length === 0) return;

    setIsSyncing(true);
    try {
      const successfulSyncs = [];
      
      for (const sale of offlineSales) {
        try {
          await axios.post(`${baseURL}/get-sales`, sale, {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            timeout: 5000
          });
          successfulSyncs.push(sale.id);
        } catch (error) {
          console.error("Failed to sync sale:", sale.id, error);
          break;
        }
      }

      if (successfulSyncs.length > 0) {
        const updatedOfflineSales = offlineSales.filter(
          sale => !successfulSyncs.includes(sale.id)
        );
        setOfflineSales(updatedOfflineSales);
        localStorage.setItem(OFFLINE_SALES_KEY, JSON.stringify(updatedOfflineSales));
      }
    } finally {
      setIsSyncing(false);
      fetchSales(1, true);
    }
  };

// Update the search effect
useEffect(() => {
  if (debouncedSearchTerm) {
    if (isOnline) {
      // When online, fetch fresh results from server
      fetchSales(1, true);
    } else {
      // When offline, filter locally
      const combined = [
        ...allSales,
        ...offlineSales.map(sale => ({
          ...sale,
          id: sale.id,
          ref: `OFFLINE-${sale.id.replace('offline_', '')}`,
          customer_name: sale.customer?.name || "Guest",
          total_amount: sale.total_amount || 0,
          paid_amount: sale.paid_amount || sale.amount_paid || 0,
          balance: sale.balance || 0,
          change_amount: sale.change_amount || 0,
          created_at: sale.created_at || sale.timestamp || new Date().toISOString(),
          is_offline: sale.is_offline || false,
          is_refunded: sale.is_refunded || false,
          is_partially_refunded: sale.is_partially_refunded || false
        }))
      ];
      
      const term = debouncedSearchTerm.toLowerCase();
      setFilteredSales(combined.filter(sale => {
        const searchFields = [
          sale.customer_name?.toLowerCase(),
          String(sale.id),
          sale.ref?.toLowerCase(),
          sale.total_amount?.toString(),
          format(new Date(sale.created_at), "dd/MM/yyyy HH:mm").toLowerCase()
        ];
        return searchFields.some(field => field?.includes(term));
      }));
    }
  } else {
    // When search is cleared, show all sales
    fetchSales(1, true);
  }
}, [debouncedSearchTerm, isOnline]);

// Fix the date formatting in SalesRow
{/* <td>{format(new Date(allSales.created_at), "dd/MM/yyyy HH:mm")}</td> */}

// Update the fetchSales function to handle search properly
const fetchSales = async (pageNum = 1, reset = false) => {
  if (isLoading) return;
  
  setIsLoading(true);
  try {
    const params = {
      page: pageNum,
      limit: PAGE_SIZE,
    };

    if (isOnline && debouncedSearchTerm) {
      params.search = debouncedSearchTerm;
    }

    const response = await axios.get(`${baseURL}/get-sales`, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      params,
      timeout: 5000
    });

    if (response.data?.status && response.data.data?.sales) {
      const newSales = response.data.data.sales;
      
      if (reset) {
        setAllSales(newSales);
        localStorage.setItem(SALES_CACHE_KEY, JSON.stringify(newSales));
        // Force update filtered sales when resetting
        setFilteredSales(newSales);
      } else {
        const updatedSales = [...allSales, ...newSales];
        setAllSales(updatedSales);
        localStorage.setItem(SALES_CACHE_KEY, JSON.stringify(updatedSales));
        // Update filtered sales with new data
        setFilteredSales(updatedSales);
      }

      setHasMore(response.data.data.has_more || newSales.length === PAGE_SIZE);
      setPage(pageNum);
    }
  } catch (error) {
    console.error("Failed to fetch sales:", error);
    if (!isOnline || error.message.includes('Network Error')) {
      const cachedSales = JSON.parse(localStorage.getItem(SALES_CACHE_KEY)) || [];
      setAllSales(cachedSales);
      setFilteredSales(cachedSales);
    }
  } finally {
    setIsLoading(false);
  }
};

const handleSearch = (term) => {
  setSearchTerm(term);
  if (term.length === 0) {
    // When clearing search, reload initial data
    fetchSales(1, true);
  }
  // No else needed - the debounced effect will handle the search
};

  const openRefundModal = (sale) => {
    if (sale.is_offline) {
      alert("Offline sales cannot be refunded until synced with server");
      return;
    }
    setSelectedSale(sale);
    setShowRefundModal(true);
  };

  const toggleTheme = () => {
    const newTheme = theme === "light" ? "dark" : "light";
    setTheme(newTheme);
    localStorage.setItem("theme", newTheme);
  };

  const SalesRow = ({ sale, openRefundModal, theme }) => {
    // Helper function to safely format currency
    const formatCurrency = (value) => {
      if (value === null || value === undefined) return '£0.00';
      const num = typeof value === 'string' ? parseFloat(value) : value;
      return isNaN(num) ? '£0.00' : `£${num.toFixed(2)}`;
    };
  
    return (
      <tr>
        <td>{sale.id}</td>
        <td>{sale.ref}</td>
        <td> { console.log("Sale Customer Name:", sale)}
          {sale.customer_name || "N/A"}
          {sale.is_refunded && <Badge bg="danger" className="ms-1">Refunded</Badge>}
          {sale.is_partially_refunded && !sale.is_refunded && (
            <Badge bg="warning" text="dark" className="ms-1">Partial</Badge>
          )}
        </td>
        <td>{formatCurrency(sale.total_amount)}</td>
        <td>{formatCurrency(sale.paid_amount)}</td>
        <td className={sale.balance > 0 ? "text-danger fw-bold" : ""}>
          {formatCurrency(sale.balance)}
        </td>
        <td>{formatCurrency(sale.change_amount)}</td>
        <td>{format(new Date(sale.created_at), "dd/MM/yyyy HH:mm")}</td>
        <td>
          {sale.is_offline ? (
            <Badge bg="secondary">
              <FaBan className="me-1" /> Offline
            </Badge>
          ) : (
            <Badge bg="success">
              <FaWifi className="me-1" /> Online
            </Badge>
          )}
        </td>
        <td>
          <Button
            size="sm"
            variant="outline-primary"
            className="me-2"
            onClick={() => window.open(`/receipt/${sale.id}`, "_blank")}
          >
            <FaPrint />
          </Button>
          {!sale.is_refunded && !sale.is_offline && (
            <Button 
              size="sm"
              variant="outline-danger"
              onClick={() => openRefundModal(sale)}
            >
              <FaUndo />
            </Button>
          )}
        </td>
      </tr>
    );
  };

  return (
    <div className={`sales-list ${theme}`}>
      <RefundModal
        show={showRefundModal}
        onHide={() => {
          setShowRefundModal(false);
          fetchSales(1, true);
        }}
        saleItem={selectedSale}
      />

      <TopNavbar user={user} toggleTheme={toggleTheme} theme={theme} />
      
      <Container fluid className={`vh-100 ${theme === "dark" ? "bg-dark text-white" : "bg-light text-dark"}`}>
        <h4>Sales Records</h4>

        {!isOnline && (
  <Alert variant="warning" className="mb-3">
    <FaBan className="me-2" />
    You are currently offline. Showing cached data and offline sales.
  </Alert>
)}

        {isSyncing && (
          <Alert variant="info" className="mb-3">
            <Spinner animation="border" size="sm" className="me-2" />
            Syncing offline sales with server...
          </Alert>
        )}

        <Form.Group className="mb-3">
          <Form.Control
            type="text"
            placeholder="Search by customer, ID, reference, amount or date..."
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
          />
        </Form.Group>

        {searchTerm && (
  <Alert variant="info" className="mb-3 d-flex align-items-center">
    {isOnline ? (
      <>
        <FaSearch className="me-2" />
        Showing search results for: <strong className="ms-1">"{searchTerm}"</strong>
      </>
    ) : (
      <>
        <FaBan className="me-2" />
        Showing offline results for: <strong className="ms-1">"{searchTerm}"</strong>
      </>
    )}
  </Alert>
)}

        <Table striped bordered hover responsive className={theme === "dark" ? "table-dark" : ""}>
          <thead>
            <tr>
              <th colSpan="10" className="text-end">
              <Badge bg={isOnline ? "success" : "warning"} className="me-2">
  {isOnline ? (
    <>
      <FaWifi className="me-1" /> Online
    </>
  ) : (
    <>
      <FaBan className="me-1" /> Offline
    </>
  )}
</Badge>
              </th>
            </tr>
            <tr>
              <th>Sale ID</th>
              <th>Ref</th>
              <th>Customer</th>
              <th>Total</th>
              <th>Paid</th>
              <th>Balance</th>
              <th>Change</th>
              <th>Date</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
  {isLoading ? (
    <tr>
      <td colSpan="10" className="text-center">
        <Spinner animation="border" variant="primary" className="me-2" />
        Loading sales...
      </td>
    </tr>
  ) : filteredSales.length > 0 ? (
    filteredSales.map((sale) => (
      <SalesRow 
        key={sale.id}
        sale={sale}
        openRefundModal={openRefundModal}
        theme={theme}
      />
    ))
  ) : (
    <tr>
      <td colSpan="10" className="text-center">
        <FaSearch className="me-2" />
        {searchTerm ? `No results found for "${searchTerm}"` : "No sales found"}
      </td>
    </tr>
  )}
</tbody>
        </Table>

        {hasMore && !searchTerm && (
  <div className="text-center mt-3">
    <Button 
      variant="primary" 
      onClick={() => {
        setSearchTerm(''); // Clear search when loading more
        fetchSales(page + 1);
      }}
      disabled={isLoading}
    >
      {isLoading ? (
        <>
          <Spinner animation="border" size="sm" /> Loading...
        </>
      ) : (
        "Load More"
      )}
    </Button>
  </div>
)}
      </Container>
    </div>
  );
};

export default SalesList;